import { useEffect } from 'react';
import * as z from 'zod';
import { useNavigate, getRouteApi } from '@tanstack/react-router';

import { NAVBAR_ICON_URL } from '@/constants';

import config from '@/config';
import { AUTH_KEY } from '@/views/home/auth/Authentication.hooks';
import DateToken from '@/views/date-token/DateToken';

import styles from './Callback.module.css';

import { useCallback } from './App.hooks';

const callbackRouteApi = getRouteApi('/callback');

export const callbackSearchSchema = z.object({
	e: z.string().optional(),
	id_token: z.string().optional(),
});

type CallbackSearch = z.infer<typeof callbackSearchSchema>;

export const Callback = () => {
	const { e, id_token }: CallbackSearch = callbackRouteApi.useSearch();
	if (config?.app?.auth?.enabled && config.app?.auth.type === 'date-token') {
		return <DateToken code={e} />;
	}

	return <DefaultCallback token={id_token} />;
};

function DefaultCallback({ token }: { token?: string }) {
	const navigate = useNavigate();
	const { isLoading, error, isActive } = useCallback({ token });

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (isActive) {
			setTimeout(() => {
				localStorage.setItem(AUTH_KEY, 'true');
				navigate({
					to: '/',
					replace: true,
				});
			}, 2000);
			return;
		}

		const invalidAuthenticationUrl =
			config.build.links?.invalidAuthenticationUrl;
		if (invalidAuthenticationUrl) {
			window.location.href = invalidAuthenticationUrl;
		}
	}, [isActive, isLoading]);

	if (error) {
		return <div style={{ color: 'black' }}>{error.message}</div>;
	}
	if (isLoading) {
		return <Loading />;
	}

	if (!isActive) {
		return <div />;
	}

	return <Loading />;
}

function Loading() {
	return (
		<div className={styles['loading']}>
			<img
				src={NAVBAR_ICON_URL}
				alt="Logo"
				className="animate__animated animate__pulse animate__fast animate__infinite"
			/>
		</div>
	);
}
